import { Button, useDisclosure } from '@chakra-ui/react'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'

import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { WizardPropertyAdditionalData as Step4FormValues, WizardPropertyAdditionalData } from 'BrokerTsLib/src/dtos'
import { InitialPolicyHolderAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { PolicyHolderPropertyStep4ValidationSchema } from 'BrokerTsLib/src/validation'
import _ from 'lodash'
import { useContext, useEffect, useState } from 'react'
import * as yup from 'yup'
import ErrorSummary from '../../components/ErrorSummary'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { Loading } from '../../components/Loading'
import { MainContent } from '../../components/MainContent'
import { useGetStep4 } from '../../hooks/useWizard'
import { Save } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { AdditionalInsureePropertyInfo } from '../AdditionalInsureePropertyInfo'
import { FormButtons } from '../FormButtons'
import { AdditionalPropertyInfoCpp } from './AdditionalPropertyInfoCpp'
import { AdditionalPropertyInfoDirect } from './AdditionalPropertyInfoDirect'
import { AdditionalPropertyInfoGcp } from './AdditionalPropertyInfoGcp'
import { AdditionalPropertyInfoKb } from './AdditionalPropertyInfoKb'
import { AdditionalPropertyInfoFloorAreas } from './AdditionalPropertyInfoKoop'


interface Step4FormProps {
  onSubmit: Function
}

const getDefaultvalues = (data?: Step4FormValues) => {
  return {
    policyHolder: InitialPolicyHolderAdditionalData,
    ...data
  }
}

export const schema = yup.object().shape(
  {
    policyHolder: PolicyHolderPropertyStep4ValidationSchema,
  }
)

export const Step4Form = ({ onSubmit }: Step4FormProps) => {
  const { insuranceParams, insuranceParamsId } = useContext(insuranceParamsContext)
  const isContract = !!insuranceParams?.generalParams.contractNumber

  const { isLoading, isFetching, data: axiosResponse, refetch } = useGetStep4(insuranceParamsId ?? '', {
    enabled: false,
    cacheTime: 0,
    onSuccess: (values: any) => {
      reset(getDefaultvalues(values.data))
    }
  })

  const data = axiosResponse?.data as WizardPropertyAdditionalData

  useEffect(() => {
    refetch()
  }, [])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose
  } = useDisclosure()

  const methods = useForm<Step4FormValues>({
    defaultValues: getDefaultvalues(data),
    resolver: yupResolver(schema) as any
  })
  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, errors },
    setError
  } = methods

  const [isSubmitError, setIsSubmitError] = useState(false)

  const submitForm = handleSubmit(data => {
    const values = _.cloneDeep(data)
    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den    
    if (values.property?.additionalDataGcp?.startConstructionDate) {
      values.property.additionalDataGcp.startConstructionDate = formatDate(values.property.additionalDataGcp.startConstructionDate)
    }
    

    return onSubmit(values)
      .then(() => {
        setIsSubmitError(false)

        if (values.nextPage == 'current') {
          savedFormAlertOnOpen()
          refetch()
        }
      })
      .catch((err: any) => {
        setIsSubmitError(true)

        if (!(err.response && err.response.status === 400 && err.response.data)) {
          savedFormAlertOnOpen()
          return
        }

        const data = err.response.data
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  if (isLoading)
    return null

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          {!(isLoading || isFetching) && data?.property?.additionalDataRequired &&
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Doplňující informace o předmětu pojištění</FormSectionTitle>

                <FormSectionDescription>
                  Doplňte dodatečné informace nutné pro sjednání pojištění.
                </FormSectionDescription>
              </FormSectionHeader>

              {data.insurerId == Insurer.CPP &&
                <FormSectionBody>
                  <AdditionalPropertyInfoCpp />
                </FormSectionBody>
              }

              {data.insurerId == Insurer.GeneraliCeskaPojistovna &&
                <FormSectionBody>
                  <AdditionalPropertyInfoGcp />
                </FormSectionBody>
              }

              {(data.insurerId == Insurer.Kooperativa || data.insurerId == Insurer.Csob) &&
                <FormSectionBody>
                  <AdditionalPropertyInfoFloorAreas />
                </FormSectionBody>
              }

              {data.insurerId == Insurer.Direct &&
                <FormSectionBody>
                  <AdditionalPropertyInfoDirect />
                </FormSectionBody>
              }

              {data.insurerId == Insurer.KB &&
                <FormSectionBody>
                  <AdditionalPropertyInfoKb />
                </FormSectionBody>
              }

            </FormSection>
          }

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Doplňující informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>
                Doplňte informace o pojistníkovi a dalších osobách pro sjednání pojištění.
              </FormSectionDescription>
            </FormSectionHeader>

            {!data?.policyHolder && (isLoading || isFetching) && <Loading large={true} />}
            {data?.policyHolder &&
              <FormSectionBody>
                {data.policyHolder && <AdditionalInsureePropertyInfo />}
              </FormSectionBody>
            }
          </FormSection>

          <ErrorSummary errors={errors} />

          {!isContract &&
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}>
                    Uložit
                  </Button>
                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Save />
                    Uložit a pokračovat na shrnutí
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    colorScheme={!isSubmitError ? 'primary' : 'red'}
                    header={!isSubmitError ? 'Uloženo' : 'Chyba'}
                    body={!isSubmitError ? 'Údaje byly úspěšně uloženy' : 'Při ukládání došlo k chybě'}
                  />
                </>
              }
            />
          }
        </MainContent>
      </form>
    </FormProvider>
  )
}
